import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'

// sessionStorage (not localStorage) so the invite token is dropped when the
// tab closes — limits the window an XSS or extension has to replay it.
const INVITE_TOKEN_STORAGE_KEY = 'pending_invite_token'
const invite_storage = (): Storage =>
  typeof window !== 'undefined' ? window.sessionStorage : ({} as Storage)

/**
 * Landing page when mom taps the invite link the owner sent her.
 *
 * Behavior:
 *  - Reads ?token=… from the URL and stashes it in localStorage so that
 *    after the Google OAuth round-trip, AuthCallback can find it and
 *    call accept_consignee_invite() on her behalf.
 *  - If she's ALREADY signed in (rare — invited owner), call the RPC
 *    directly and route to /mom.
 *  - Otherwise show a single "Continue with Google" button.
 */
export default function AcceptInvite() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { user, loading } = useAuth()
  const [networkError, setNetworkError] = useState('')
  const [busy, setBusy] = useState(false)
  const token = params.get('token')
  // Derived error: missing token is structurally invalid, not a transient
  // network failure, so we compute it instead of pushing it through setState
  // inside an effect (react-hooks/set-state-in-effect).
  const error = !token ? 'Invalid invite link — no token in the URL.' : networkError

  useEffect(() => {
    if (!token) return
    invite_storage().setItem(INVITE_TOKEN_STORAGE_KEY, token)
  }, [token])

  // If user is already signed in, accept immediately.
  useEffect(() => {
    const acceptNow = async () => {
      if (loading || !user || !token) return
      setBusy(true)
      try {
        const { error: rpcError } = await supabase.rpc('accept_consignee_invite', {
          p_token: token,
        })
        if (rpcError) throw rpcError
        invite_storage().removeItem(INVITE_TOKEN_STORAGE_KEY)
        navigate('/mom', { replace: true })
      } catch (e) {
        setNetworkError((e as Error).message)
      } finally {
        setBusy(false)
      }
    }
    void acceptNow()
  }, [user, loading, token, navigate])

  const signInWithGoogle = async () => {
    setNetworkError('')
    setBusy(true)
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/auth/callback` },
    })
    if (oauthError) {
      setNetworkError(oauthError.message)
      setBusy(false)
    }
    // On success the browser redirects to Google; nothing more to do here.
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // See SignIn.tsx for why 100dvh — same iOS URL-bar visibility issue.
        minHeight: '100dvh',
        padding: 'var(--space-4)',
      }}
    >
      <div
        style={{
          maxWidth: 380,
          width: '100%',
          background: 'var(--color-surface)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-8)',
          boxShadow: 'var(--shadow-md)',
        }}
      >
        <h1 style={{ fontSize: 'var(--font-size-2xl)', marginBottom: 'var(--space-4)' }}>
          You&apos;re invited
        </h1>
        <p style={{ color: 'var(--color-text-muted)', marginBottom: 'var(--space-6)' }}>
          Sign in with Google to start logging sales. Your account links to the shop owner&apos;s
          data — you&apos;ll only see the products you&apos;re holding.
        </p>

        {error && (
          <p style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-4)' }}>
            {error}
          </p>
        )}

        {user ? (
          <p style={{ color: 'var(--color-text-muted)' }}>
            {busy ? 'Accepting invite…' : 'Redirecting…'}
          </p>
        ) : (
          <button
            type="button"
            onClick={signInWithGoogle}
            disabled={busy || !token}
            style={{
              width: '100%',
              padding: 'var(--space-3)',
              background: 'white',
              color: '#1a1a1a',
              border: '1px solid #dadce0',
              borderRadius: 'var(--radius-md)',
              fontWeight: 600,
              cursor: busy ? 'not-allowed' : 'pointer',
              opacity: busy ? 0.6 : 1,
            }}
          >
            {busy ? 'Sending…' : 'Continue with Google'}
          </button>
        )}
      </div>
    </div>
  )
}
